import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { memo, useEffect, useRef } from 'react';
import { Animated, View } from 'react-native';
import { aiColors } from '../../../../../ai-suite/theme';
import {
  LiveAreaBar,
  LiveCoachingEdge,
} from '../../../functions/useCoachingEdge';
import { ReactionSlot } from './ReactionSlot';

const kBarWidth = 48;

/**
 * On phone, one line above the controls: the coach's reaction on the left,
 * the area that last moved and by how much on the right. Fixed height, so
 * the thread never jumps once it shows; with nothing to show yet, it is not
 * there.
 */
export const CoachingStrip = memo(({ edge }: { edge: LiveCoachingEdge }) => {
  const { rpx } = useResponsiveContext();
  const [areas] = useBehaviorSubject(edge.areas$);
  const [reaction] = useBehaviorSubject(edge.reaction$);
  const [capped] = useBehaviorSubject(edge.capped$);
  // The area that moved last stays until another one does.
  const last = useRef<LiveAreaBar | null>(null);
  const moved = areas.find(x => x.delta);
  if (moved) last.current = moved;
  else if (last.current)
    last.current = {
      ...(areas.find(x => x.area === last.current!.area) ?? last.current),
      delta: last.current.delta,
    };
  const bar = last.current;
  const fill = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.spring(fill, {
      toValue: (bar?.score ?? 0) / 100,
      friction: 7,
      useNativeDriver: true,
    }).start();
  }, [bar?.score, fill]);
  const width = rpx(kBarWidth);
  const up = (bar?.delta ?? 0) >= 0;
  if (!reaction && !capped && !bar) return null;
  return (
    <View
      style={{
        height: rpx(40),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: rpx(12),
        paddingHorizontal: rpx(16),
        borderTopWidth: 1,
        borderTopColor: aiColors.border,
        backgroundColor: aiColors.card,
      }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        {reaction ? (
          <ReactionSlot reaction={reaction} />
        ) : capped ? (
          <Typography.Text size={rpx(11)} color={aiColors.destructive}>
            ★ 这次最高一星
          </Typography.Text>
        ) : null}
      </View>
      {bar ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(6),
            opacity: capped ? 0.45 : 1,
          }}
        >
          <Typography.Text size={rpx(10)} color={aiColors.mutedForeground}>
            {bar.name}
          </Typography.Text>
          <View
            style={{
              width,
              height: rpx(6),
              borderRadius: rpx(3),
              overflow: 'hidden',
              backgroundColor: aiColors.muted,
            }}
          >
            <Animated.View
              style={{
                height: '100%',
                borderRadius: rpx(3),
                backgroundColor: up ? aiColors.green : aiColors.amber,
                transform: [
                  {
                    translateX: fill.interpolate({
                      inputRange: [0, 1],
                      outputRange: [-width, 0],
                    }),
                  },
                ],
              }}
            />
          </View>
          {bar.delta ? (
            <Typography.Text
              size={rpx(10)}
              bold
              color={up ? aiColors.green : aiColors.amber}
            >
              {up ? `+${bar.delta}` : `${bar.delta}`}
            </Typography.Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
});
